import { useRef, useCallback } from 'react';
import QRCode from 'react-qr-code';
import { cn } from "@/lib/utils";
import { QrCode, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

export const QRDisplay = ({ 
  value,
  title,
  subtitle,
  size = 'md',
  showDownload = false,
  onDownload,
  className 
}) => {
  const qrRef = useRef(null);
  
  const sizeClasses = {
    sm: { container: 'w-32 h-32', qrSize: 96 },
    md: { container: 'w-48 h-48', qrSize: 160 },
    lg: { container: 'w-64 h-64', qrSize: 224 },
    xl: { container: 'w-80 h-80', qrSize: 288 },
  };
  
  const sizeConfig = sizeClasses[size] || sizeClasses.md;
  
  // Download QR as PNG
  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
      return;
    }
    
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = sizeConfig.qrSize + 32;
      canvas.height = sizeConfig.qrSize + 32;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 16, 16, sizeConfig.qrSize, sizeConfig.qrSize);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `pass-${value?.slice(-8) || 'qr'}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [value, sizeConfig.qrSize, onDownload]);
  
  return (
    <div 
      className={cn(
        "bg-graphite rounded-xl border border-white/[0.06] p-6 flex flex-col items-center",
        className
      )}
      data-testid="qr-display"
    >
      {title && (
        <h3 className="text-lg font-medium text-cloud-white mb-1">{title}</h3>
      )}
      {subtitle && (
        <p className="text-sm text-soft-silver mb-4">{subtitle}</p>
      )}
      
      {/* QR Code Container */}
      <div 
        ref={qrRef}
        className={cn(
          "rounded-xl bg-white p-4 mb-4 flex items-center justify-center",
          sizeConfig.container
        )}
      >
        {value ? (
          <QRCode
            value={value}
            size={sizeConfig.qrSize}
            level="H"
            bgColor="#ffffff"
            fgColor="#0A0A0B"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
            <QrCode className="w-16 h-16 text-gray-400" />
          </div>
        )}
      </div>
      
      {value && (
        <p className="text-xs text-muted-text font-mono mb-4 max-w-full truncate px-2">
          {value.length > 20 ? `${value.slice(0, 10)}...${value.slice(-10)}` : value}
        </p>
      )}
      
      {showDownload && value && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          className="border-white/[0.06] hover:bg-white/[0.05] text-soft-silver"
          data-testid="qr-download"
        >
          <Download className="w-4 h-4 mr-2" />
          Download QR
        </Button>
      )}
    </div>
  );
};

export default QRDisplay;
